import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../components/ToastProvider';
import { api } from '../services/api';
import type { TariffListItem } from '../services/types';
import { notifyApiResult } from '../utils/notify';
import { formatDate } from '../utils/format';
import { useAuth } from '../hooks/useAuth';

const PERIODS = ['1', '3', '6', '12'] as const;
const PERIOD_LABELS: Record<string, string> = {
  '1': '1 мес',
  '3': '3 мес',
  '6': '6 мес',
  '12': '12 мес',
};

export function TariffsPage() {
  const { user } = useAuth();
  const toast = useToast();
  const canWrite = user?.role === 'superadmin';

  const [tariffs, setTariffs] = useState<TariffListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tier, setTier] = useState('default');
  const [region, setRegion] = useState('ru');
  const [period, setPeriod] = useState('1');
  const [price, setPrice] = useState('75');
  const [days, setDays] = useState('30');

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editPrice, setEditPrice] = useState('');
  const [editDays, setEditDays] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.listTariffs();
    if (res.data) setTariffs(res.data);
    else if (res.error) {
      setError(res.error.message);
      toast.error(res.error.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tariffKey = (t: Pick<TariffListItem, 'tier' | 'region' | 'period'>) =>
    `${t.tier}:${t.region}:${t.period}`;

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const res = await api.upsertTariff({
      tier,
      region,
      period,
      price: Number(price),
      days: Number(days),
    });
    if (notifyApiResult(toast, res, (data) => `Тариф ${data.tier}/${data.region}/${data.period} сохранён`)) {
      load();
    } else if (res.error) {
      setError(res.error.message);
    }
  };

  const startEdit = (t: TariffListItem) => {
    setEditingKey(tariffKey(t));
    setEditPrice(String(t.price));
    setEditDays(String(t.days));
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditPrice('');
    setEditDays('');
  };

  const saveEdit = async (t: TariffListItem) => {
    setError(null);
    const res = await api.upsertTariff({
      tier: t.tier,
      region: t.region,
      period: t.period,
      price: Number(editPrice),
      days: Number(editDays),
    });
    if (notifyApiResult(toast, res, () => 'Тариф обновлён')) {
      cancelEdit();
      load();
    } else if (res.error) {
      setError(res.error.message);
    }
  };

  const remove = async (t: TariffListItem) => {
    if (!window.confirm(`Удалить тариф ${t.tier} / ${t.region} / ${PERIOD_LABELS[t.period] || t.period}?`)) {
      return;
    }
    setError(null);
    const res = await api.deleteTariff(t.tier, t.region, t.period);
    if (notifyApiResult(toast, res, () => 'Тариф удалён')) {
      if (editingKey === tariffKey(t)) cancelEdit();
      load();
    } else if (res.error) {
      setError(res.error.message);
    }
  };

  const currency = (r: string) => (r === 'ru' ? '₽' : '$');

  return (
    <div>
      <div className="page-header">
        <h1>Тарифы</h1>
      </div>

      <p style={{ color: 'var(--text-muted)', marginTop: 0, maxWidth: '40rem' }}>
        Цены и длительности из таблицы <code>tariffs</code>. Сайт читает их сразу; бот
        подхватывает из БД в течение ~10 секунд. Intro-цена Premium (первый месяц) задаётся
        в коде, не здесь.
      </p>

      {canWrite && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>Создать / обновить</h3>
          <form onSubmit={submitCreate} className="filters-row">
            <select className="select" value={tier} onChange={(e) => setTier(e.target.value)}>
              <option value="default">Default</option>
              <option value="premium">Premium</option>
            </select>
            <select className="select" value={region} onChange={(e) => setRegion(e.target.value)}>
              <option value="ru">ru (₽)</option>
              <option value="en">en ($)</option>
            </select>
            <select className="select" value={period} onChange={(e) => setPeriod(e.target.value)}>
              {PERIODS.map((p) => (
                <option key={p} value={p}>
                  {PERIOD_LABELS[p]}
                </option>
              ))}
            </select>
            <input
              className="input"
              type="number"
              step="0.01"
              min="0.01"
              placeholder="Цена"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              required
            />
            <input
              className="input"
              type="number"
              min="1"
              placeholder="Дней"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              required
            />
            <button type="submit" className="btn btn-primary">
              Сохранить
            </button>
          </form>
        </div>
      )}

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {loading && <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>}

      {!loading && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Регион</th>
                <th>Период</th>
                <th>Цена</th>
                <th>Дней</th>
                <th>Обновлён</th>
                {canWrite && <th></th>}
              </tr>
            </thead>
            <tbody>
              {tariffs.length === 0 && (
                <tr>
                  <td colSpan={canWrite ? 7 : 6} style={{ color: 'var(--text-muted)' }}>
                    Тарифов в БД нет (сайт использует fallback из кода)
                  </td>
                </tr>
              )}
              {tariffs.map((t) => {
                const key = tariffKey(t);
                const isEditing = editingKey === key;
                return (
                  <tr key={key}>
                    <td>{t.tier}</td>
                    <td>
                      {t.region} ({currency(t.region)})
                    </td>
                    <td>{PERIOD_LABELS[t.period] || t.period}</td>
                    <td>
                      {isEditing ? (
                        <input
                          className="input"
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={editPrice}
                          onChange={(e) => setEditPrice(e.target.value)}
                          style={{ width: '6.5rem' }}
                        />
                      ) : (
                        <>
                          {t.price} {currency(t.region)}
                        </>
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          className="input"
                          type="number"
                          min="1"
                          value={editDays}
                          onChange={(e) => setEditDays(e.target.value)}
                          style={{ width: '5rem' }}
                        />
                      ) : (
                        t.days
                      )}
                    </td>
                    <td>{formatDate(t.updated_at)}</td>
                    {canWrite && (
                      <td style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                        {isEditing ? (
                          <>
                            <button type="button" className="btn btn-primary" onClick={() => saveEdit(t)}>
                              OK
                            </button>
                            <button type="button" className="btn btn-ghost" onClick={cancelEdit}>
                              Отмена
                            </button>
                          </>
                        ) : (
                          <>
                            <button type="button" className="btn btn-ghost" onClick={() => startEdit(t)}>
                              Изменить
                            </button>
                            <button type="button" className="btn btn-ghost" onClick={() => remove(t)}>
                              Удалить
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
